import 'yet-another-react-lightbox/styles.css';
import 'keen-slider/keen-slider.min.css';
import type { AppProps } from 'next/app';
import { I18nextProvider } from 'react-i18next';
import Script from 'next/script';
import dynamic from 'next/dynamic';
import { ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { loadDevMessages } from '@apollo/client/dev';
import { Bridge } from '@digitaz/bridge-sdk-client';
import '@/utils/bugsnag';
import { getServerI18n } from '@/i18n-server';
import { ClientWrapper } from '@/components/ClientWrapper';
import { GlobalStyles } from '@/styles/globalStyles';
import { BannersStyles } from '@/components/Banners/Banners.styles';
import { createApolloClient } from '@/config/apollo-client';
import { isProduction } from '@/utils/helpers';
import { ThemeProviderWrapper } from '@/hooks/theme/wrapper';
import { BridgeSafeAreaManager } from '@/bridge/components/BridgeSafeAreaManager';
import { getBridgeConfig } from '@/bridge/config';
import { Seo } from '@/components/Seo/Seo';
import { ClientSeoData, SeoFullData } from '@/api/types/seo.types';
import { PageContextProvider } from '@/contexts';
import { Device, DeviceType } from '@/utils/getDeviceType';
import { Languages } from '@/constants/common';
import { useClientI18n } from '@/hooks/useClientI18n';
import { AuthCheck } from '@/components/PubNubInit/AuthCheck';
import { PubNubInit } from '@/components/PubNubInit/PubNubInit';

const BlockerDetector = dynamic(
  () =>
    import('@/components/Banners/BlockerDetector').then(
      mod => mod.BlockerDetector
    ),
  { ssr: false }
);

if (!isProduction()) {
  loadDevMessages();
}

// export function reportWebVitals(metric: NextWebVitalsMetric) {
//   if (
//     typeof window === 'undefined' ||
//     window.location.pathname !== ROUTES.home
//   ) {
//     return;
//   }
//
//   const isAuthenticated =
//     localStorage.getItem(STORAGE_KEYS.AUTH_STATUS) ===
//     AuthStatus.Authenticated.toString();
//
//   if (metric.name === 'CLS' && metric.value >= 0.2) {
//     try {
//       const windowSize = `${window.innerWidth}x${window.innerHeight}`;
//       const metricStr = JSON.stringify(metric);
//       const message = `[CLS] window size: ${windowSize}, scrollY: ${window.scrollY}, isAuthenticated: ${isAuthenticated}, metric: ${metricStr}`;
//       logError(message);
//     } catch (e) {
//       console.error(e);
//     }
//   }
// }

function App({
  Component,
  pageProps,
}: AppProps<{
  currentLang: Languages;
  isSSRWebView: boolean;
  seoFullData: SeoFullData;
  clientSeoData?: ClientSeoData;
  apolloState?: NormalizedCacheObject | null;
  accessToken?: string;
  device: Device;
  deviceType: DeviceType;
}>) {
  const isBrowser = typeof window !== 'undefined';
  const i18nInstance = useClientI18n(pageProps.currentLang);

  const apolloClient = createApolloClient({
    lang: pageProps.currentLang || i18nInstance.language,
    initialState: pageProps.apolloState,
  });

  return (
    <>
      <Script src="/scripts/adriver.core.2.min.js" />
      <Seo {...pageProps.seoFullData} />
      <Bridge
        isServerWebView={pageProps.isSSRWebView}
        config={getBridgeConfig()}
      >
        <BridgeSafeAreaManager />
        <ThemeProviderWrapper isWebView={pageProps.isSSRWebView}>
          <GlobalStyles />
          <BannersStyles />
          <I18nextProvider
            i18n={
              isBrowser ? i18nInstance : getServerI18n(pageProps.currentLang)
            }
          >
            <ApolloProvider client={apolloClient}>
              <PageContextProvider
                device={pageProps.device}
                deviceType={pageProps.deviceType}
              >
                <ClientWrapper
                  isSSRWebView={pageProps.isSSRWebView}
                  accessToken={pageProps.accessToken}
                >
                  <AuthCheck />
                  <PubNubInit />
                  <BlockerDetector />
                  <Component {...pageProps} />
                </ClientWrapper>
              </PageContextProvider>
            </ApolloProvider>
          </I18nextProvider>
        </ThemeProviderWrapper>
      </Bridge>
    </>
  );
}

export default App;
